import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  RptSumReviewRow,
  getRptOpinions,
  getRptSumRows,
  getRptTargets,
  saveRptOpinions,
  saveRptSumReview,
} from 'apis/ut/UtMatrixComparisonReportList';
import { useCommonModal } from 'hooks/useCommonModal';
import useSessionStore from 'stores/useSessionStore';
import { CommonUtil } from 'utils/CommonUtil';
import { RptOpinion } from 'models/ut/UtMatrixComparisonReportList';
import { UT_DB_CODE, UT_TYPES, findUtType } from '../constants';
import { useUtComparisonStore } from '../store/useUtComparisonStore';
import { RptSumItem } from '../types';
import { OpinionSaveHandle } from './AnalysisOpinionSection';
import SectionBox from './SectionBox';

/**
 * ⑥ UT 항목별 분석결과 — TB_EELMB_UTM_RPT_OPIN_M의
 * `ATC_DIVS_CD='MECH'` + `ANLS_OPIN_TP_CD IN ('CDA','N2','IW','PCW','ST','NG','RA','EA','SA')` 9건.
 *
 * 항목 하나가 "분석 결과"(`ANLS_RLT_CTN`) 한 칸이다. 자동 생성될 자리지만 사용자가 직접
 * 고쳐 쓸 수 있다 — ⑦의 AI 분석 칸과 같은 방식이다.
 *
 * ⚠ 항목별 "검토 의견"(OPIN_CTN) 칸은 두지 않는다. 검토 의견은 ⑦ 하단의 종합의견
 *   한 칸으로 모았다. 다만 예전에 입력된 OPIN_CTN 값이 남아 있을 수 있어, 저장할 때
 *   건드리지 않고 그대로 돌려보낸다.
 *
 * ⚠ 생성 로직(룰베이스)은 아직 확정되지 않아 FE에 없다. 나중에 생성기가 붙어
 *   `ANLS_RLT_CTN`을 다시 만들면 여기서 수정한 내용은 덮어써진다(현재 합의된 동작).
 *
 * 항목 코드는 화면 키(Steam/Return/…)와 DB 코드(ST/RA/…)가 섞여 들어올 수 있어
 * findUtType()으로 양쪽 다 받는다. 새로 만드는 행은 DB 코드로 저장한다.
 *
 * 저장된 리포트를 열었을 때(historyRptId)만 노출된다 — ⑦ OpinionSection과 같은 조건이다.
 *
 * 자체 저장 버튼은 두지 않는다. 화면의 저장 버튼은 ⑦ 하단 하나뿐이고, 그쪽이 ref로
 * 이 섹션의 save()를 직접 호출한다(UtComparisonPage에서 ref를 연결).
 */
const MechUtAnalysisSection = forwardRef<OpinionSaveHandle>(function MechUtAnalysisSection(
  _props,
  ref
) {
  const historyRptId = useUtComparisonStore((s) => s.historyRptId);
  const rptId = historyRptId ?? '';
  const userId = useSessionStore((s) => s.userId);
  const { openCommonModal } = useCommonModal();

  const [opinions, setOpinions] = useState<RptOpinion[]>([]);
  /** UtKey → 분석 결과 (ANLS_RLT_CTN) */
  const [analysisDrafts, setAnalysisDrafts] = useState<Record<string, string>>({});
  /**
   * 사용자가 실제로 입력한 항목(UtKey) — 저장 시 이 항목만 내보낸다.
   *
   * draft가 비어 있지 않은지로 판단하면, 조회 시점에 내용이 없어 ''로 들어온 항목이
   * 그 뒤 리포트 생성기(규칙 기반)로 DB에 채워졌을 때 저장 순간 ''로 덮어써진다.
   */
  const touchedRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!rptId) return;
    let alive = true;
    setLoading(true);
    setError('');
    getRptOpinions(rptId)
      .then((rows) => {
        if (!alive) return;
        setOpinions(rows);
        // 조회 결과를 UtKey 기준 draft로 펼친다. 사용자가 편집 중인 항목은 건드리지 않는다
        setAnalysisDrafts((prev) => {
          const next = { ...prev };
          rows
            .filter((o) => o.atcDivsCd === 'MECH')
            .forEach((o) => {
              const ut = findUtType(o.anlsOpinTpCd);
              if (ut && next[ut.key] === undefined) next[ut.key] = o.anlsRltCtn ?? '';
            });
          return next;
        });
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [rptId]);

  /** 이 리포트에 이미 저장돼 있는 UT 항목 키 */
  const savedUtKeys = useMemo(() => {
    const set = new Set<string>();
    opinions
      .filter((o) => o.atcDivsCd === 'MECH')
      .forEach((o) => {
        const ut = findUtType(o.anlsOpinTpCd);
        if (ut) set.add(ut.key);
      });
    return set;
  }, [opinions]);

  /**
   * 덮어쓰기 확인.
   *
   * 모달이 콜백 방식이라 Promise로 감싼다. 창을 X로 닫아도 confirm 타입은 noCallback을
   * 타므로(CommonModal.tsx) 응답 없이 멈추는 경우는 없다.
   */
  const confirmOverwrite = (names: string[]) =>
    new Promise<boolean>((resolve) => {
      openCommonModal({
        modalType: 'confirm',
        content:
          `이미 내역이 입력된 항목이 ${names.length}건 있습니다.<br/>` +
          `(${names.join(', ')})<br/>덮어쓰시겠습니까?`,
        yesCallback: () => resolve(true),
        noCallback: () => resolve(false),
      });
    });

  /**
   * 분석결과 → 분석 Report 탭의 「UT 사용량 정합화 검토 내역 > 내역」(SUM_M.RVW_CTN) 반영.
   *
   * - **내용이 있는 항목만** 대상이다. 빈 분석결과로 기존 내역을 지우면 안 된다.
   * - 이미 다른 내용이 들어 있는 항목이 하나라도 있으면 항목명을 나열해 한 번만 묻는다.
   * - saveRptSumReview는 넘긴 행으로 덮어쓰므로, 조회한 전체 행을 그대로 돌려보내고
   *   기계 UT 행의 rvwCtn만 교체한다(투자비 영향·향후 계획·전기 행 보존).
   *
   * ⚠ BE의 저장이 UPDATE라, SUM_M에 행이 없는 UT 항목은 반영되지 않는다.
   */
  const syncReviewNotes = async () => {
    const texts = new Map<string, string>();
    UT_TYPES.forEach((ut) => {
      const text = (analysisDrafts[ut.key] ?? '').trim();
      if (text) texts.set(ut.key, text);
    });
    if (texts.size === 0) return;

    const [sumRows, targets] = await Promise.all([getRptSumRows(rptId), getRptTargets(rptId)]);
    const tgtUtmId = targets.find((t) => t.role === 'TGT')?.utmId ?? '';
    if (!tgtUtmId) throw new Error('비교대상 UT Matrix를 찾을 수 없습니다.');

    /** 이 행에 새로 쓸 내역 — 대상이 아니면 undefined */
    const nextOf = (row: RptSumItem): string | undefined => {
      if (row.atcDivsCd !== 'MECH') return undefined;
      const ut = findUtType(row.utmTpCd);
      return ut ? texts.get(ut.key) : undefined;
    };

    const conflicts = sumRows.filter((r) => {
      const next = nextOf(r);
      return !!next && !!r.rvwCtn.trim() && r.rvwCtn.trim() !== next;
    });
    if (conflicts.length > 0) {
      const names = conflicts.map((r) => findUtType(r.utmTpCd)?.label ?? r.utmTpNm);
      const ok = await confirmOverwrite(names);
      if (!ok) return;
    }

    const rows: RptSumReviewRow[] = sumRows.map((r) => ({
      atcDivsCd: r.atcDivsCd,
      utmTpCd: r.utmTpCd,
      infcRvwCtn: r.infcRvwCtn,
      rvwCtn: nextOf(r) ?? r.rvwCtn,
      nextPlanCtn: r.nextPlanCtn,
    }));
    await saveRptSumReview(rptId, tgtUtmId, rows, userId);
  };

  /**
   * 저장 — ⑦ 하단의 저장 버튼이 ref로 호출한다.
   *
   * saveRptOpinions가 리포트 전체를 덮어쓰는 방식이라, 저장 직전에 최신 상태를 다시 읽어
   * 이 영역 밖의 행(⑦ AI 분석 항목·종합의견)을 그대로 되돌려 보낸다. 같은 버튼 안에서
   * 순서대로 저장되는 다른 영역의 내용을 지우지 않기 위함이다.
   *
   * 오류는 삼키지 않고 던진다 — 호출한 쪽(saveAll)이 한 번에 알린다.
   */
  const save = async () => {
    if (!rptId || !userId) return;
    const latest = await getRptOpinions(rptId).catch(() => opinions);

    const merged: RptOpinion[] = latest.map((o) => {
      if (o.atcDivsCd !== 'MECH') return o;
      const ut = findUtType(o.anlsOpinTpCd);
      if (!ut) return o; // 공통코드 기반 AI 분석 항목은 손대지 않는다
      // 사용자가 손대지 않은 항목은 latest(저장 직전 재조회값)를 그대로 되돌린다 —
      // 화면이 들고 있던 ''로 자동 생성 결과를 지우지 않기 위함이다(touchedRef 주석 참고).
      if (!touchedRef.current.has(ut.key)) return o;
      // OPIN_CTN은 화면에 칸이 없으므로 저장된 값을 그대로 둔다
      return { ...o, anlsRltCtn: analysisDrafts[ut.key] ?? '' };
    });

    const existingKeys = new Set<string>();
    latest
      .filter((o) => o.atcDivsCd === 'MECH')
      .forEach((o) => {
        const ut = findUtType(o.anlsOpinTpCd);
        if (ut) existingKeys.add(ut.key);
      });
    // 저장된 적 없는 항목에 내용을 새로 쓴 경우 행을 만들어 붙인다 (DB 코드로 저장)
    const added: RptOpinion[] = UT_TYPES.filter(
      (ut) => !existingKeys.has(ut.key) && !!analysisDrafts[ut.key]
    ).map((ut, idx) => ({
      rptId,
      sortOrd: merged.length + idx + 1,
      atcDivsCd: 'MECH',
      anlsOpinTpCd: UT_DB_CODE[ut.key],
      anlsRltCtn: analysisDrafts[ut.key] ?? '',
      aiAnsCtn: '',
      opinCtn: '',
    }));

    const nextRows = [...merged, ...added];
    await saveRptOpinions(rptId, nextRows, userId);
    setOpinions(nextRows);

    /*
      검토 내역 반영은 분석결과 저장이 끝난 뒤에 한다. 여기서 실패해도 위 저장은 이미
      완료됐고, 호출한 쪽(saveAll)의 남은 저장(종합의견)까지 막으면 안 되므로 오류를
      던지지 않고 여기서 알린다.
    */
    try {
      await syncReviewNotes();
    } catch (e) {
      window.alert('검토 내역 반영 오류: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  // 저장 버튼은 ⑦ 하단 하나뿐이라, 부모가 ref로 이 save()를 호출한다.
  // 조기 반환(!rptId)보다 위에 있어야 훅 순서가 깨지지 않는다.
  useImperativeHandle(ref, () => ({ save }));

  if (!rptId) return null;

  return (
    <SectionBox no="⑥" title="UT 항목별 분석결과" count={UT_TYPES.length}>
      {loading && <p className="hint-text">UT 항목별 분석결과를 불러오는 중…</p>}
      {error && <p className="error-text">UT 항목별 분석결과 조회 오류: {error}</p>}
      {!loading &&
        !error &&
        UT_TYPES.map((ut) => (
          <div key={ut.key} className="anls-item">
            {/* label에 이미 항목명 + 단위가 들어 있다 (예: 'CDA [Nl/min]') */}
            <p className="anls-item-title">{ut.label}</p>
            <p className="anls-label">분석 결과</p>
            <textarea
              rows={4}
              value={CommonUtil.safeHtmlContents(analysisDrafts[ut.key] ?? '', false)}
              placeholder={
                savedUtKeys.has(ut.key)
                  ? ''
                  : '아직 생성된 내용이 없습니다. 직접 입력할 수 있습니다.'
              }
              onChange={(e) => {
                touchedRef.current.add(ut.key);
                setAnalysisDrafts((prev) => ({
                  ...prev,
                  [ut.key]: CommonUtil.safeHtmlContents(e.target.value, false),
                }));
              }}
            />
          </div>
        ))}
    </SectionBox>
  );
});

export default MechUtAnalysisSection;
